"use client"

import { useEffect } from "react"

type GlobalErrorProps = {
	error: Error & { digest?: string }
	reset: () => void
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
	useEffect(() => {
		console.error("Global error:", error)
	}, [error])

	return (
		<html lang="en" className="dark">
			<body
				className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden antialiased"
				style={{
					backgroundColor: "#030303",
					color: "#ededed",
					fontFamily:
						'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
					backgroundImage:
						"linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)",
					backgroundSize: "40px 40px",
				}}
			>
				{/* Scan line effect */}
				<div
					className="pointer-events-none fixed inset-0 z-10"
					style={{
						background:
							"repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(244, 63, 94, 0.03) 2px, rgba(244, 63, 94, 0.03) 4px)",
					}}
				/>

				{/* Red radial glow for error state */}
				<div
					className="pointer-events-none fixed inset-0"
					style={{
						background:
							"radial-gradient(ellipse at center, rgba(244, 63, 94, 0.1) 0%, transparent 60%)",
					}}
				/>

				<div className="relative z-20 text-center max-w-lg">
					{/* Error code display */}
					<div className="mb-8">
						<div
							className="inline-flex items-center gap-3 px-4 py-2 rounded-full mb-6"
							style={{
								border: "1px solid #222222",
								backgroundColor: "rgba(14, 14, 14, 0.5)",
							}}
						>
							<span
								style={{
									width: "6px",
									height: "6px",
									borderRadius: "50%",
									background: "rgb(244, 63, 94)",
									boxShadow: "0 0 8px rgb(244, 63, 94)",
									animation: "pulse 2s ease-in-out infinite",
								}}
							/>
							<span
								style={{
									fontFamily: "ui-monospace, monospace",
									fontSize: "0.75rem",
									color: "#888888",
									letterSpacing: "0.05em",
									textTransform: "uppercase",
								}}
							>
								System Error
							</span>
						</div>

						<h1
							style={{
								fontFamily: "ui-monospace, monospace",
								fontSize: "8rem",
								lineHeight: 1,
								fontWeight: 700,
								letterSpacing: "-0.05em",
								color: "rgba(244, 63, 94, 0.2)",
								textShadow: "0 0 20px rgba(244, 63, 94, 0.5)",
								userSelect: "none",
							}}
						>
							500
						</h1>
					</div>

					{/* Diagnostic panel */}
					<div
						className="mb-8 text-left"
						style={{
							backgroundColor: "#0e0e0e",
							border: "1px solid #222222",
							borderRadius: "0.75rem",
							padding: "1.5rem",
						}}
					>
						<div
							className="flex items-center gap-2 mb-4 pb-3"
							style={{ borderBottom: "1px solid #222222" }}
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="#888888"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
								<path d="M12 9v4" />
								<path d="M12 17h.01" />
							</svg>
							<span
								style={{
									fontFamily: "ui-monospace, monospace",
									fontSize: "0.75rem",
									color: "#888888",
								}}
							>
								EXCEPTION CAUGHT
							</span>
						</div>

						<div
							style={{
								fontFamily: "ui-monospace, monospace",
								fontSize: "0.875rem",
							}}
						>
							<div
								className="flex justify-between mb-3"
								style={{ gap: "1rem" }}
							>
								<span style={{ color: "#888888" }}>Status</span>
								<span style={{ color: "rgb(244, 63, 94)" }}>
									INTERNAL_ERROR
								</span>
							</div>
							<div
								className="flex justify-between mb-3"
								style={{ gap: "1rem" }}
							>
								<span style={{ color: "#888888" }}>Type</span>
								<span style={{ color: "#ededed" }}>Unhandled Exception</span>
							</div>
							{error.digest && (
								<div className="flex justify-between" style={{ gap: "1rem" }}>
									<span style={{ color: "#888888" }}>Digest</span>
									<span
										style={{ color: "#555555", fontFamily: "ui-monospace" }}
									>
										{error.digest}
									</span>
								</div>
							)}
						</div>
					</div>

					<p
						className="mb-8"
						style={{ color: "#888888", fontSize: "0.875rem" }}
					>
						Something went wrong on our end. Our team has been notified. Please
						try again or return home.
					</p>

					<div className="flex gap-3 justify-center">
						<button
							type="button"
							onClick={reset}
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "0.5rem",
								padding: "0.625rem 1rem",
								fontSize: "0.875rem",
								fontWeight: 500,
								borderRadius: "0.5rem",
								border: "1px solid #222222",
								backgroundColor: "transparent",
								color: "#ededed",
								cursor: "pointer",
								transition: "all 0.2s",
							}}
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
								<path d="M3 3v5h5" />
								<path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
								<path d="M16 16h5v5" />
							</svg>
							Try again
						</button>
						<a
							href="/"
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "0.5rem",
								padding: "0.625rem 1rem",
								fontSize: "0.875rem",
								fontWeight: 500,
								borderRadius: "0.5rem",
								border: "none",
								backgroundColor: "rgb(16, 185, 129)",
								color: "#030303",
								textDecoration: "none",
								cursor: "pointer",
								boxShadow:
									"0 0 20px rgba(16, 185, 129, 0.3), 0 0 40px rgba(16, 185, 129, 0.1)",
								transition: "all 0.2s",
							}}
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
								<polyline points="9 22 9 12 15 12 15 22" />
							</svg>
							Home
						</a>
					</div>
				</div>

				<style>{`
					@keyframes pulse {
						0%, 100% { opacity: 1; }
						50% { opacity: 0.5; }
					}
					button:hover {
						background-color: #141414 !important;
						border-color: #333333 !important;
					}
					a:hover {
						box-shadow: 0 0 25px rgba(16, 185, 129, 0.4), 0 0 50px rgba(16, 185, 129, 0.2) !important;
					}
				`}</style>
			</body>
		</html>
	)
}
